/**
 * @description
 * Authentication types for OAuth providers (Google, GitHub) and JWT token management.
 * Defines user sessions, OAuth state, and authentication flow types.
 *
 * @author AI Developer <ai@byteable.ai>
 * @date January 6, 2025
 * @see /admin/src/lib/auth.ts
 */

/**
 * Environment types
 */
export type Environment = 'development' | 'production' | 'preview';

/**
 * Supported OAuth providers
 */
export type OAuthProvider = 'google' | 'github';

/**
 * User information from OAuth providers
 */
export interface User {
  id: string;
  email: string;
  name: string;
  avatar?: string;
  provider: OAuthProvider;
  role?: 'admin' | 'user';
  createdAt: string;
  lastLoginAt: string;
}

/**
 * JWT token payload structure
 */
export interface JWTPayload {
  sub: string; // User ID
  email: string;
  name: string;
  provider: OAuthProvider;
  avatar?: string;
  role?: 'admin' | 'user';
  dev?: boolean; // Development token flag
  iat: number; // Issued at
  exp: number; // Expiration
}

/**
 * Development authentication configuration
 */
export interface DevAuthConfig {
  jwtSecret: string;
  adminUser: User;
}

/**
 * Authentication session stored in KV
 */
export interface AuthSession {
  userId: string;
  email: string;
  name: string;
  provider: OAuthProvider;
  accessToken: string;
  refreshToken?: string;
  expiresAt: number;
  createdAt: number;
}

/**
 * OAuth state parameter for CSRF protection
 */
export interface OAuthState {
  provider: OAuthProvider;
  redirectUrl: string;
  nonce: string;
  createdAt: number;
}

/**
 * OAuth callback query parameters
 */
export interface OAuthCallbackParams {
  code: string;
  state: string;
  error?: string;
  error_description?: string;
}

/**
 * Google OAuth token response
 */
export interface GoogleTokenResponse {
  access_token: string;
  expires_in: number;
  refresh_token?: string;
  scope: string;
  token_type: string;
  id_token: string;
}

/**
 * Google user info response
 */
export interface GoogleUserInfo {
  id: string;
  email: string;
  verified_email: boolean;
  name: string;
  given_name: string;
  family_name: string;
  picture: string;
  locale: string;
}

/**
 * GitHub OAuth token response
 */
export interface GitHubTokenResponse {
  access_token: string;
  token_type: string;
  scope: string;
}

/**
 * GitHub user info response
 */
export interface GitHubUserInfo {
  id: number;
  login: string;
  name: string;
  email: string;
  avatar_url: string;
  bio: string;
  company: string;
  location: string;
  created_at: string;
}

/**
 * GitHub email response
 */
export interface GitHubEmail {
  email: string;
  primary: boolean;
  verified: boolean;
  visibility: string | null;
}

/**
 * Authentication context value
 */
export interface AuthContextValue {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  login: (provider: OAuthProvider) => Promise<void>;
  logout: () => Promise<void>;
  refreshSession: () => Promise<void>;
}

/**
 * Authentication error types
 */
export type AuthErrorType =
  | 'invalid_token'
  | 'expired_token'
  | 'invalid_state'
  | 'oauth_error'
  | 'network_error'
  | 'unauthorized';

/**
 * Authentication error
 */
export class AuthError extends Error {
  constructor(
    public type: AuthErrorType,
    message: string,
    public details?: unknown
  ) {
    super(message);
    this.name = 'AuthError';
  }
}

/*
 * © 2025 Byte Inc. All rights reserved.
 * This code is the confidential and proprietary information of Byte Inc.
 * You shall not disclose such Confidential Information and shall use it only in
 * accordance with the terms of the license agreement you entered into with Byte Inc.
 */